/**
 * Integrytas Group — Common UI
 *
 * In-app PDF viewer that does NOT depend on the browser's PDF plugin.
 *
 * An <iframe src={pdfUrl}> only works when the browser is willing to RENDER a
 * PDF. Chrome hands PDFs to an external application whenever
 * `plugins.always_open_pdf_externally` is set — common on managed profiles —
 * and then the frame paints an empty grey panel, with no error, no console
 * message and no blocked-framing header to explain it. So pages are rasterised
 * here with pdf.js onto a canvas instead, and nothing about the outcome depends
 * on a browser setting.
 *
 * Two things the file itself forces on us:
 *   • The bytes are fetched ONCE and handed over as an ArrayBuffer. Given a URL,
 *     pdf.js streams with HTTP Range requests, which a signed Storage URL does
 *     not serve well.
 *   • `standardFontDataUrl` is required. Our reports draw in the standard-14
 *     Helvetica, which pdf-lib does not embed; without the font data pdf.js
 *     warns and then hangs partway through the document.
 *
 * Loading and rasterising are SEPARATE effects so changing the zoom re-paints
 * from the document already in memory instead of re-fetching it.
 *
 * pdf.js is loaded with a DYNAMIC import so its ~1.7 MB (worker included) is
 * fetched only when someone opens a document, and never enters the main bundle.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, Maximize2, Printer, Download } from 'lucide-react';

/** The subset of pdf.js this component depends on. */
interface PdfPage {
  getViewport(o: { scale: number }): { width: number; height: number };
  render(o: Record<string, unknown>): { promise: Promise<void> };
}
interface PdfDoc {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
}

/**
 * Print resolution, independent of the on-screen zoom — what someone files
 * should not depend on how far they happened to have zoomed in. 150 DPI against
 * the PDF's 72pt user space, which is enough for report tables and keeps a
 * 26-page document to a few MB of JPEG rather than the ~900 MB of live canvas
 * that 300 DPI would need all at once.
 */
const PRINT_SCALE = 150 / 72;
const PRINT_JPEG_QUALITY = 0.92;

/**
 * The print document: one image per page, at the report's OWN paper size.
 *
 * Pure and exported so it can be tested without a browser — the @page rule and
 * the page-break handling are the parts that quietly produce blank sheets or
 * shrink a landscape report onto portrait paper, and neither is visible until
 * someone prints.
 */
export function buildPrintDocument(
  images: readonly string[],
  widthPt: number,
  heightPt: number,
  title: string,
): string {
  const safeTitle = title.replace(/[<>&]/g, '');
  return '<!doctype html><html><head><meta charset="utf-8"><title>' + safeTitle +
    '</title><style>' +
    `@page { size: ${Math.round(widthPt)}pt ${Math.round(heightPt)}pt; margin: 0; }` +
    'html,body{margin:0;padding:0;background:#fff}' +
    'img{display:block;width:100%;height:auto;break-after:page;page-break-after:always}' +
    'img:last-of-type{break-after:auto;page-break-after:auto}' +
    '</style></head><body>' +
    images.map(src => `<img src="${src}">`).join('') +
    '</body></html>';
}

export interface PdfViewerProps {
  /** Short-lived signed URL for the artifact. */
  url: string;
  /** Width one page occupies at 100%, in CSS pixels. */
  width?: number;
  /** Height of the scroll area. */
  maxHeight?: string;
  /** Name for the saved file. Falls back to a generic one. */
  fileName?: string;
  className?: string;
}

/**
 * pdf.js keeps ONE worker for the whole page (GlobalWorkerOptions), so
 * loadingTask.destroy() tears down machinery a concurrently-loading document is
 * still using — and React StrictMode double-invokes effects in development.
 * Only the newest session may destroy.
 */
let session = 0;

/** Rasterise at 2x so text stays crisp on HiDPI without being extravagant. */
const DEVICE_SCALE = 2;
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const STANDARD_FONTS_URL = '/pdfjs/standard_fonts/';

export function PdfViewer({ url, width = 900, maxHeight = '70vh', fileName, className = '' }: PdfViewerProps) {
  const { t } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  // A copy of the bytes kept for Save. Taken BEFORE pdf.js gets the buffer,
  // which may transfer it to the worker and leave the original detached — and
  // so Save never depends on the signed URL still being valid.
  const bytesRef = useRef<Blob | null>(null);
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [zoom, setZoom] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── load the document (url only — zooming must not refetch) ────────────────
  useEffect(() => {
    const mySession = ++session;
    let cancelled = false;
    let release: (() => void) | null = null;
    setDoc(null);
    setError(null);

    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = (
          await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
        ).default;

        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
        const data = await resp.arrayBuffer();
        if (cancelled) return;
        bytesRef.current = new Blob([data.slice(0)], { type: 'application/pdf' });

        // destroy() lives on the LOADING TASK, not the document proxy.
        const loadingTask = pdfjs.getDocument({ data, standardFontDataUrl: STANDARD_FONTS_URL });
        release = () => { if (session === mySession) void loadingTask.destroy(); };
        const loaded = await loadingTask.promise;
        if (cancelled) { release(); return; }
        setDoc(loaded as unknown as PdfDoc);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => { cancelled = true; release?.(); };
  }, [url]);

  // ── rasterise at the current zoom ──────────────────────────────────────────
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !doc) return;
    let cancelled = false;
    host.replaceChildren();

    void (async () => {
      const scale = (width * zoom / (await doc.getPage(1)).getViewport({ scale: 1 }).width) * DEVICE_SCALE;
      for (let n = 1; n <= doc.numPages; n++) {
        if (cancelled) return;
        const canvas = document.createElement('canvas');
        try {
          const page = await doc.getPage(n);
          if (cancelled) return;
          const viewport = page.getViewport({ scale });
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        } catch {
          // One unrenderable page must not cost the reader the other twenty-five.
        }
        if (cancelled) return;
        canvas.style.width = `${width * zoom}px`;
        canvas.style.height = 'auto';
        canvas.style.maxWidth = 'none';
        canvas.className = 'mx-auto mb-3 rounded-csm-control border border-csm-border bg-white elev-1';
        canvas.dataset.pdfPage = String(n);
        host.appendChild(canvas);
      }
    })();

    return () => { cancelled = true; };
  }, [doc, zoom, width]);

  /**
   * Hand the browser a print document built from the pages we rasterise
   * ourselves.
   *
   * The usual approach — print a hidden iframe pointed at the PDF — needs the
   * browser to RENDER PDFs, and the whole reason this viewer exists is that it
   * may not: a browser set to open PDFs externally prints nothing at all. So the
   * pages are drawn here and laid out with @page at the document's own size, so
   * orientation and paper size follow the report rather than the print dialog's
   * defaults.
   *
   * Pages are converted to JPEG and their canvas released one at a time; holding
   * 26 live canvases at print resolution is hundreds of megabytes.
   */
  const print = async () => {
    if (!doc || printing) return;
    setPrinting(true);
    setError(null);
    try {
      const first = await doc.getPage(1);
      const shape = first.getViewport({ scale: 1 });
      const images: string[] = [];
      for (let n = 1; n <= doc.numPages; n++) {
        const page = await doc.getPage(n);
        const viewport = page.getViewport({ scale: PRINT_SCALE });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        // JPEG has no alpha, so an unpainted background would come out black.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        images.push(canvas.toDataURL('image/jpeg', PRINT_JPEG_QUALITY));
        canvas.width = 0;   // release the backing store before the next page
        canvas.height = 0;
      }

      const win = window.open('', '_blank');
      if (!win) throw new Error(t('common.pdf.print_blocked', 'The print window was blocked. Allow pop-ups for this site and try again.'));
      win.document.write(buildPrintDocument(images, shape.width, shape.height, fileName || document.title));
      win.document.close();

      // Print only once every image has actually DECODED. readyState flips to
      // "complete" the moment document.write finishes, long before the data URLs
      // are decoded, and printing then yields a dialog previewing blank sheets.
      const pending = [...win.document.images].map(img => (
        img.complete
          ? Promise.resolve()
          : new Promise<void>(res => {
              img.addEventListener('load', () => res(), { once: true });
              img.addEventListener('error', () => res(), { once: true });
            })
      ));
      await Promise.all(pending);

      // An image that decoded to nothing prints as a blank sheet, which looks
      // like the report is empty rather than like something failed. Say so.
      const blank = [...win.document.images].filter(img => img.naturalWidth === 0);
      if (blank.length > 0) {
        win.close();
        throw new Error(t('common.pdf.print_failed', 'The pages could not be prepared for printing. Save the document and print it from your PDF reader.'));
      }

      // One more frame so the layout that print() snapshots is the decoded one.
      await new Promise<void>(res => win.requestAnimationFrame(() => res()));
      win.focus();
      win.print();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPrinting(false);
    }
  };

  /**
   * Save the document. Uses the copy already in memory rather than re-fetching:
   * the signed URL is short-lived, and someone who has had the report open for
   * a few minutes would otherwise get an expiry error instead of a file.
   */
  const save = () => {
    const blob = bytesRef.current;
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || t('common.pdf.default_file', 'document.pdf');
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick — Safari cancels the download if the URL dies
    // while the click is still being handled.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  };

  const stepZoom = (dir: 1 | -1) => setZoom(z => {
    const i = ZOOM_STEPS.findIndex(s => s >= z - 0.001);
    const next = ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, i + dir))];
    return next ?? z;
  });

  const btn = 'flex h-7 w-7 items-center justify-center rounded-csm-control border border-csm-border bg-white text-csm-ink hover:bg-csm-surface-alt disabled:opacity-40';

  return (
    <div className={className}>
      {error && (
        <div className="mb-2 rounded-csm-control border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
          {error}
        </div>
      )}

      <div className="mb-2 flex items-center gap-1.5">
        <button type="button" onClick={() => stepZoom(-1)} disabled={zoom <= ZOOM_STEPS[0]}
          className={btn} aria-label={t('common.pdf.zoom_out', 'Zoom out')} title={t('common.pdf.zoom_out', 'Zoom out')}>
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="w-12 text-center text-csm-xs tabular-nums text-csm-ink-soft">{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => stepZoom(1)} disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
          className={btn} aria-label={t('common.pdf.zoom_in', 'Zoom in')} title={t('common.pdf.zoom_in', 'Zoom in')}>
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => setZoom(1)}
          className="ml-1 rounded-csm-control border border-csm-border bg-white px-2 py-1 text-csm-xs hover:bg-csm-surface-alt"
          title={t('common.pdf.fit', 'Fit width')}>
          <Maximize2 className="mr-1 inline h-3 w-3" />{t('common.pdf.fit', 'Fit width')}
        </button>
        <button type="button" onClick={save} disabled={!doc}
          className="ml-1 rounded-csm-control border border-csm-border bg-white px-2 py-1 text-csm-xs hover:bg-csm-surface-alt disabled:opacity-40"
          title={t('common.pdf.save', 'Save')}>
          <Download className="mr-1 inline h-3 w-3" />{t('common.pdf.save', 'Save')}
        </button>
        <button type="button" onClick={() => void print()} disabled={!doc || printing}
          className="ml-1 rounded-csm-control border border-csm-border bg-white px-2 py-1 text-csm-xs hover:bg-csm-surface-alt disabled:opacity-40"
          title={t('common.pdf.print', 'Print')}>
          <Printer className="mr-1 inline h-3 w-3" />
          {printing ? t('common.pdf.preparing', 'Preparing…') : t('common.pdf.print', 'Print')}
        </button>
        {doc && (
          <span className="ml-auto text-csm-xs text-csm-muted">
            {t('common.pdf.pages', { count: doc.numPages, defaultValue_one: '{{count}} page', defaultValue_other: '{{count}} pages' })}
          </span>
        )}
      </div>

      <div className="overflow-auto rounded-csm-control bg-csm-surface-alt p-3" style={{ maxHeight }}>
        {!error && !doc && (
          <div className="py-8 text-center text-csm-sm text-csm-muted">
            {t('common.pdf.loading', 'Rendering document…')}
          </div>
        )}
        <div ref={hostRef} />
      </div>
    </div>
  );
}
